import React, { useEffect } from 'react';
import { FormLabel, Grid, Divider, Collapse } from '@mui/material';
import { Control, Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { FormFields } from 'utils/types';
import { useTranslate } from 'hooks/use-translate';
import { useFieldProps } from 'hooks';
import { AppInput } from 'components/app-input';
import { AppSelect } from 'components/app-select';
import { useSourceWhatsAppTemplates } from 'hooks/use-source-whats-app-templates';
import { FormModel } from '../../models';
import { ServiceWhatsAppContents } from 'services/whats-app-contents';
import style from './index.module.scss';
interface Props {
  isLoading: boolean;
  templatesPayload: Record<string, any>;
}

export const Form: React.FC<React.PropsWithChildren<Props>> = ({ isLoading, templatesPayload }) => {
  const { t } = useTranslate();

  const sourceTemplates = useSourceWhatsAppTemplates();

  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext<FormFields<FormModel>>();

  const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });

  // The field array keeps the hand-written FormModel (FormFields<> makes `variables` opaque for the path typing).
  const { fields } = useFieldArray<FormModel, 'variables'>({
    control: control as unknown as Control<FormModel>,
    name: 'variables',
  });

  const templateID = watch('whatsAppContentID');

  const template = React.useMemo(
    () => sourceTemplates.data.find((t) => t.id === templateID),
    [sourceTemplates.data, templateID],
  );

  useEffect(() => {
    if (!template) return;

    const variables = ServiceWhatsAppContents.makeVariables(template.variables, templatesPayload);

    setValue('variables', variables);
  }, [template, setValue, templatesPayload]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Controller
            control={control}
            name={'toPhoneNumber'}
            render={({ field: renderProps }) => (
              <AppInput
                {...getFieldProps(renderProps)}
                label={t('mobile-phone')}
                disabled={isLoading}
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            control={control}
            name={'whatsAppContentID'}
            render={({ field: renderProps }) => {
              return (
                <AppSelect
                  {...getFieldProps(renderProps)}
                  disabled={isLoading}
                  disableClearable
                  options={sourceTemplates.data}
                  loading={sourceTemplates.loading}
                />
              );
            }}
          />
        </Grid>
      </Grid>
      <Collapse in={fields.length > 0}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Divider className={style.divider} />
          </Grid>
          <Grid size={12}>
            <div style={{ paddingBottom: '10px' }}>
              <FormLabel>{t('variables')}</FormLabel>
            </div>
          </Grid>
          {fields.map((field, index) => {
            return (
              <Grid key={field.id} size={12}>
                <Controller
                  control={control}
                  name={`variables.${index}.value`}
                  defaultValue={field.value}
                  render={({ field: renderProps }) => {
                    const props = getFieldProps(renderProps, { withLabel: false });

                    return (
                      <AppInput
                        {...props}
                        label={(field as { title?: string }).title} // `title` comes from the API payload, not the schema (pre-existing)
                        variant={'outlined'}
                        size={'small'}
                      />
                    );
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </Collapse>
    </>
  );
};
